import React, { useEffect, useMemo, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { Check, ChevronsUpDown, CalendarDays, Send } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { SOUTH_AFRICAN_BANKS, BANK_ACCOUNT_RULES, validateSAIdNumber } from '@/utils/copied';
import { COUNTRIES, countryLabel } from '@/utils/countries';
import {
  formatEmployerName, formatPersonName, formatJobTitle, formatAddress, formatDigits, formatCellphone, formatPassport, formatAsylumPermit,
  hasStreetDesignation, expandStreetAbbreviation, isOnlyStreetType, AUTO_STREET_SUFFIX, formatDate, parseDate,
  DOB_FROM, dobTo, APPOINTMENT_FROM, appointmentTo,
  ID_TYPES, idTypeUsesSaIdNumber, idNumberLabel,
  PAY_FREQUENCIES, ACCOUNT_TYPES, HOLDER_RELATIONSHIP, padSingleDigits,
  UIF_PAYE_OPTIONS, formatAmountInput, canonicalAmount, formatRand,
  FIELD_LABELS, requiredFields, formatErrors, isSoftAccountWarning,
} from '@/utils/employeeRules';

// Layout follows the Xero Payroll "Employee" screen (Essentials / Bank Account Details /
// Residential Address); the header, fonts, hints and modals follow the TCA Debit Order Mandate
// site; the SEND button, Graph email flow and success modal follow the SARS Power of Attorney site.

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_EMAIL = 'admin@tcauditors.co.za';

const INITIAL_FORM = {
  employer_name: '',
  pay_frequency: 'Monthly',
  first_names: '',
  last_name: '',
  date_of_birth: '',
  date_of_appointment: '',
  id_type: ID_TYPES.RSA_ID,
  id_number: '',
  passport_country: '',
  income_tax_number: '',
  job_title: '',
  email: '',
  cellphone: '',
  alt_cellphone: '',
  alt_email: '',
  payment_method: 'EFT',
  bank_name: '',
  account_number: '',
  branch_code: '',
  account_type: '',
  holder_relationship: HOLDER_RELATIONSHIP,
  unit_number: '',
  complex: '',
  street_number: '',
  street: '',
  suburb: '',
  city: '',
  postal_code: '',
  country: 'South Africa',
  gross_salary: '',
  uif_paye: 'Yes', // default Yes (owner)
};

// Order used for the "please complete" list and for scrolling to the first problem
const FIELD_ORDER = Object.keys(FIELD_LABELS);

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

// One "label | control" row, as on the Xero screen. The control is swapped for plain text
// (.pdf-value) while the PDF is captured, so the PDF shows exactly what was entered.
const Row = ({ name, label, required, optional, pdfValue, feedback, children, className }) => (
  <div className={cn('emp-row', className)} id={`row-${name}`}>
    <label className="emp-label" htmlFor={name}>
      {label}
      {required && <span className="required-asterisk"> *</span>}
      {optional && <span className="emp-optional"> (optional)</span>}
    </label>
    <div className="emp-control">
      <div className="pdf-hide">{children}</div>
      <div className="pdf-value">{pdfValue || ' '}</div>
      {feedback}
    </div>
  </div>
);

const SectionTitle = ({ children }) => <h2 className="emp-section-title">{children}</h2>;

// Calendar pop-up; the chosen date fills the field as dd/mm/yyyy.
const DateField = ({ name, value, onChange, onDone, fromDate, toDate, defaultMonth, className }) => {
  const [open, setOpen] = useState(false);
  const selected = parseDate(value);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) onDone(); }}>
      <PopoverTrigger asChild>
        <button type="button" id={name} data-testid={`input-${name}`} className={cn('emp-input emp-trigger', className)}>
          <span className={value ? '' : 'emp-placeholder'}>{value || 'dd/mm/yyyy'}</span>
          <CalendarDays className="emp-trigger-icon" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected || undefined}
          defaultMonth={selected || defaultMonth}
          onSelect={(d) => {
            if (!d) return;
            onChange(formatDate(d));
            setOpen(false);
            onDone();
          }}
          captionLayout="dropdown-buttons"
          fromDate={fromDate}
          toDate={toDate}
          classNames={{
            caption_label: 'hidden',
            caption_dropdowns: 'emp-cal-dropdowns',
            dropdown: 'emp-cal-select',
            vhidden: 'sr-only',
            day_selected: 'emp-cal-selected',
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

// Plain (not fuzzy) matching: entries starting with the search first (country code / bank name),
// then a word starting with it, then containing it anywhere. Fuzzy matching ranked e.g.
// "Congo (DEmocratic repUblic)" above "DEU - Germany".
const searchScore = (value, search) => {
  const v = value.toLowerCase();
  const s = search.toLowerCase().trim();
  if (!s) return 1;
  if (v.startsWith(s)) return 1;
  if (v.split(/[\s(-]+/).some((word) => word.startsWith(s))) return 0.7;
  return v.includes(s) ? 0.4 : 0;
};

// Searchable dropdown (same Popover + Command pattern as the Debit Order Mandate bank picker)
const SearchSelect = ({ name, selectedKey, display, placeholder, searchPlaceholder, emptyText, items, onSelect, onDone, className }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) onDone(); }}>
      <PopoverTrigger asChild>
        <button type="button" id={name} role="combobox" aria-expanded={open} data-testid={`input-${name}`} className={cn('emp-input emp-trigger', className)}>
          <span className={display ? '' : 'emp-placeholder'}>{display || placeholder}</span>
          <ChevronsUpDown className="emp-trigger-icon" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="emp-popover p-0" align="start">
        <Command filter={searchScore}>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.key}
                  value={item.label}
                  onSelect={() => { onSelect(item); setOpen(false); onDone(); }}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', selectedKey === item.key ? 'opacity-100' : 'opacity-0')} />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const BANK_ITEMS = SOUTH_AFRICAN_BANKS.map((b) => ({ key: b.label, label: b.label, branchCode: b.branchCode }));
const COUNTRY_ITEMS = COUNTRIES.map((c) => ({ key: c.code, label: c.label }));

// ---------------------------------------------------------------------------

const EmployeeForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [streetAutoAppended, setStreetAutoAppended] = useState(false);
  const [salaryFocused, setSalaryFocused] = useState(false); // plain number while editing, R#,###.## otherwise
  const [sending, setSending] = useState(false);
  const [problems, setProblems] = useState(null); // { title, items[] } for the "please complete" modal
  const [recipients, setRecipients] = useState(null); // success modal

  // Always start at the top of the page - also after a reload (browser scroll restoration off)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  const errors = useMemo(() => formatErrors(form), [form]);
  const required = requiredFields(form);

  const labelFor = (name) => (name === 'id_number' ? idNumberLabel(form.id_type) : FIELD_LABELS[name]);
  const isEmpty = (name) => !String(form[name] ?? '').trim();

  // Red messages appear once a field has been visited (or SEND was clicked); green ones as soon as it's valid.
  const shownError = (name) => {
    if (isEmpty(name)) {
      return required.includes(name) && (touched[name] || submitted) ? `${labelFor(name)} is required.` : '';
    }
    return touched[name] || submitted ? errors[name] || '' : '';
  };
  const isSoft = (name, message) => name === 'account_number' && isSoftAccountWarning(message);
  const isValid = (name) => !isEmpty(name) && !errors[name];

  const inputClass = (name) => {
    const err = shownError(name);
    if (err) return isSoft(name, err) ? 'warning-input' : 'input-error';
    return isValid(name) ? 'input-valid' : '';
  };

  const feedback = (name, { hint, ok } = {}) => {
    const err = shownError(name);
    if (err) {
      return <p className={cn('emp-msg no-pdf', isSoft(name, err) ? 'emp-msg-warn' : 'emp-msg-error')} data-testid={`error-${name}`}>{err}</p>;
    }
    if (isValid(name) && ok) {
      return <p className="emp-msg emp-msg-ok no-pdf" data-testid={`ok-${name}`}>{ok}</p>;
    }
    return hint ? <p className="emp-msg emp-msg-hint no-pdf">{hint}</p> : null;
  };

  const touch = (name) => setTouched((t) => (t[name] ? t : { ...t, [name]: true }));
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  // Apply a formatter as the user types and keep the caret where it was.
  const formatted = (name, formatter) => (e) => {
    const input = e.target;
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;
    const next = formatter(raw);
    const nextCaret = Math.min(formatter(raw.slice(0, caret)).length, next.length);
    setField(name, next);
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        try { input.setSelectionRange(nextCaret, nextCaret); } catch { /* not supported */ }
      }
    });
  };

  // Every field: trim leading/trailing (and double) spaces when focus is lost
  const blurTrim = (name, { lower = false, pad = false } = {}) => () => {
    setForm((f) => {
      let v = String(f[name]).trim().replace(/\s{2,}/g, ' ');
      if (lower) v = v.toLowerCase();
      if (pad) v = padSingleDigits(v);
      return v === f[name] ? f : { ...f, [name]: v };
    });
    touch(name);
  };

  // Street: " Street" is added on blur if no designation was typed, and removed again on focus
  const handleStreetFocus = () => {
    if (streetAutoAppended && form.street.endsWith(AUTO_STREET_SUFFIX)) {
      setField('street', form.street.slice(0, -AUTO_STREET_SUFFIX.length));
    }
    setStreetAutoAppended(false);
  };
  // On blur: write out an abbreviated street type ("Main Rd" -> "Main Road"), then add
  // " Street" only if there is still no street type (so never "Street Street").
  const finaliseStreet = (street) => {
    const v = expandStreetAbbreviation(street.trim().replace(/\s{2,}/g, ' '));
    return v && !hasStreetDesignation(v) && !isOnlyStreetType(v)
      ? { value: v + AUTO_STREET_SUFFIX, appended: true }
      : { value: v, appended: false };
  };
  const handleStreetBlur = () => {
    const { value, appended } = finaliseStreet(form.street);
    setField('street', value);
    setStreetAutoAppended(appended);
    touch('street');
  };

  const idFormatter = (idType) => {
    if (idTypeUsesSaIdNumber(idType)) return formatDigits(13);
    return idType === ID_TYPES.PASSPORT ? formatPassport : formatAsylumPermit;
  };

  const handleIdTypeChange = (e) => {
    const idType = e.target.value;
    setForm((f) => {
      // Switching between a 13-digit ID and a passport/permit clears the number; otherwise reformat it
      const sameKind = idTypeUsesSaIdNumber(idType) === idTypeUsesSaIdNumber(f.id_type);
      return {
        ...f,
        id_type: idType,
        id_number: sameKind ? idFormatter(idType)(f.id_number) : '',
        passport_country: idType === ID_TYPES.PASSPORT ? f.passport_country : '',
      };
    });
    setTouched((t) => ({ ...t, id_number: false, passport_country: false }));
  };

  // Calendar opens on the ID number's birth month when a valid SA ID has been entered
  const idInfo = idTypeUsesSaIdNumber(form.id_type) ? validateSAIdNumber(form.id_number) : null;
  const dobDefaultMonth = (() => {
    if (idInfo?.valid) {
      const id = form.id_number;
      const yy = Number(id.slice(0, 2));
      const year = yy <= (new Date().getFullYear() % 100) + 10 ? 2000 + yy : 1900 + yy;
      return new Date(year, Number(id.slice(2, 4)) - 1, 1);
    }
    return new Date(new Date().getFullYear() - 30, 0, 1);
  })();

  const bankRule = BANK_ACCOUNT_RULES[form.bank_name];

  // ---------------------------------------------------------------------------
  // SEND
  // ---------------------------------------------------------------------------
  const collectProblems = (f) => {
    const errs = formatErrors(f);
    const req = requiredFields(f);
    const items = [];
    FIELD_ORDER.forEach((name) => {
      const label = name === 'id_number' ? idNumberLabel(f.id_type) : FIELD_LABELS[name];
      if (!String(f[name] ?? '').trim()) {
        if (req.includes(name)) items.push({ name, text: `${label} is required.` });
      } else if (errs[name] && !isSoft(name, errs[name])) {
        items.push({ name, text: `${label}: ${errs[name]}` });
      }
    });
    return items;
  };

  const generatePdf = async (f) => {
    const element = document.querySelector('.form-wrapper');
    element.classList.add('generating-pdf');
    await new Promise((resolve) => setTimeout(resolve, 250));
    try {
      const now = new Date();
      const stamp = `Submitted on: ${now.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })} at ${now.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
      const options = {
        margin: [10, 10, 15, 10],
        filename: 'New_Employee_File.pdf',
        image: { type: 'jpeg', quality: 0.95 },
        // windowWidth: lay the form out at desktop width even when filled in on a phone
        html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1100 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        // Only rows are kept whole ('avoid-all' would push a whole section to the next page)
        pagebreak: { mode: ['css', 'legacy'], avoid: ['.emp-row', '.emp-section-title', '.letterhead-footer-container'] },
      };
      const pdf = await html2pdf().set(options).from(element).toPdf().get('pdf');
      // Page numbers + submission timestamp on every page (as on the Debit Order Mandate PDFs)
      const total = pdf.internal.getNumberOfPages();
      for (let p = 1; p <= total; p += 1) {
        pdf.setPage(p);
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${p} of ${total}`;
        pdf.text(pageText, (210 - pdf.getTextWidth(pageText)) / 2, 287);
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        pdf.text(stamp, 210 - 10 - pdf.getTextWidth(stamp), 287);
      }
      return pdf.output('datauristring').split(',')[1];
    } finally {
      element.classList.remove('generating-pdf');
    }
  };

  const handleSend = async () => {
    // The street suffix is normally added on blur; apply it here too in case it wasn't
    const { value: street, appended } = finaliseStreet(form.street);
    // Same for the leading 0 on unit / street numbers (normally added on blur)
    const unit_number = padSingleDigits(form.unit_number.trim());
    const street_number = padSingleDigits(form.street_number.trim());
    const gross_salary = canonicalAmount(form.gross_salary);
    const f = { ...form, street, unit_number, street_number, gross_salary, holder_relationship: HOLDER_RELATIONSHIP };
    if (street !== form.street || unit_number !== form.unit_number || street_number !== form.street_number
        || gross_salary !== form.gross_salary) {
      setForm(f);
      setStreetAutoAppended(appended);
    }
    setSubmitted(true);

    const items = collectProblems(f);
    if (items.length) {
      setProblems({ title: 'Please complete the form', intro: 'The form cannot be sent until the following are corrected:', items });
      return;
    }

    setSending(true);
    try {
      // Let React render the final values before capturing
      await new Promise((resolve) => setTimeout(resolve, 50));
      const pdfBase64 = await generatePdf(f);

      const payload = { ...f, pdf_base64: pdfBase64 };
      const response = await fetch(`${API_URL}/api/employee/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let detail = '';
        try { detail = (await response.json()).detail || ''; } catch { /* ignore */ }
        if (typeof detail !== 'string') detail = '';
        const message = response.status === 429
          ? 'Too many attempts in a short time. Please wait a minute and try again.'
          : `We could not send the form${detail ? `: ${detail.replace(/\.$/, '')}` : ''}. Please try again, or contact ${ADMIN_EMAIL}.`;
        setProblems({ title: 'Not sent', intro: message, items: [] });
        return;
      }
      const data = await response.json();
      setRecipients(data.recipients || []);
    } catch (error) {
      console.error('[New Employee] PDF/send error:', error);
      setProblems({ title: 'Not sent', intro: `An error occurred while preparing or sending the form. Please try again, or contact ${ADMIN_EMAIL}.`, items: [] });
    } finally {
      setSending(false);
    }
  };

  const closeProblems = () => {
    const first = problems?.items?.[0]?.name;
    setProblems(null);
    if (first) {
      // Instant jump (a smooth scroll is skipped by some browsers, e.g. in background tabs)
      document.getElementById(`row-${first}`)?.scrollIntoView({ block: 'center' });
    }
  };

  const handleThankYou = () => {
    // Start fresh at the top of the page so the same form can't be sent twice by accident
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    window.location.reload();
  };

  // ---------------------------------------------------------------------------
  // Render helpers for the common input types
  // ---------------------------------------------------------------------------
  const textInput = (name, formatter, props = {}) => (
    <input
      id={name}
      name={name}
      type="text"
      data-testid={`input-${name}`}
      value={form[name]}
      onChange={formatter ? formatted(name, formatter) : (e) => setField(name, e.target.value)}
      onBlur={blurTrim(name)}
      className={cn('emp-input', inputClass(name))}
      autoComplete="off"
      {...props}
    />
  );

  const select = (name, options, props = {}) => (
    <select
      id={name}
      name={name}
      data-testid={`input-${name}`}
      value={form[name]}
      onChange={(e) => { setField(name, e.target.value); touch(name); }}
      onBlur={() => touch(name)}
      className={cn('emp-input emp-select', inputClass(name))}
      {...props}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const idLabel = idNumberLabel(form.id_type);
  const idHint = {
    [ID_TYPES.RSA_ID]: 'Expected format: 13 digits (e.g. 9001015009086).',
    [ID_TYPES.REFUGEE]: 'Expected format: the 13-digit refugee ID number.',
    [ID_TYPES.PASSPORT]: 'As printed in the passport / foreign ID: 6-15 letters and/or digits.',
    [ID_TYPES.ASYLUM]: "As printed on the Asylum Seeker's Permit.",
  }[form.id_type];
  const idOk = idTypeUsesSaIdNumber(form.id_type)
    ? 'Looks good — valid South African ID number.'
    : 'Looks good — valid document number format.';

  return (
    <div className="app-container">
      <div className="form-wrapper">
        <div className="form-header">
          <img src="/images/TCA_Logo_Header.jpeg" alt="TC Auditors Logo" className="logo" />
          <img src="/images/TCA_Banner_Header.png" alt="Tax & Auditing Consultants" className="banner" />
          <h1 className="form-title">TCA XERO PAYROLL: NEW EMPLOYEE FILE</h1>
          <p className="form-subtitle">
            Please complete the employee's details below. Fields marked <span className="required-asterisk">*</span> are required.
          </p>
        </div>

        <div className="form-content">
          {/* ===================== Essentials ===================== */}
          <section className="emp-section">
            <SectionTitle>Essentials</SectionTitle>

            <Row name="employer_name" label="Name of Employer" required pdfValue={form.employer_name}
              feedback={feedback('employer_name', { hint: 'The company or person the employee works for, e.g. ABC Trading (Pty) Ltd.', ok: 'Looks good.' })}>
              {textInput('employer_name', formatEmployerName, { autoComplete: 'organization' })}
            </Row>

            <Row name="pay_frequency" label="Pay Frequency" required pdfValue={form.pay_frequency}>
              {select('pay_frequency', PAY_FREQUENCIES, { className: 'emp-input emp-select emp-w-md' })}
            </Row>

            <Row name="first_names" label="First Names" required pdfValue={form.first_names}
              feedback={feedback('first_names', { hint: 'All first names as on the ID document, e.g. Johannes Petrus (no accents or full stops - SARS rules).', ok: 'Looks good.' })}>
              {textInput('first_names', formatPersonName, { autoComplete: 'given-name' })}
            </Row>

            <Row name="last_name" label="Last Name" required pdfValue={form.last_name}
              feedback={feedback('last_name', { hint: 'e.g. Van Der Merwe or Smith-Jones (letters, spaces and hyphens only - no accents or full stops).', ok: 'Looks good.' })}>
              {textInput('last_name', formatPersonName, { autoComplete: 'family-name' })}
            </Row>

            <Row name="date_of_birth" label="Date of Birth" required pdfValue={form.date_of_birth}
              feedback={feedback('date_of_birth', { hint: 'Click to choose the date from the calendar.', ok: 'Looks good.' })}>
              <DateField
                name="date_of_birth"
                value={form.date_of_birth}
                onChange={(v) => setField('date_of_birth', v)}
                onDone={() => touch('date_of_birth')}
                fromDate={DOB_FROM}
                toDate={dobTo()}
                defaultMonth={dobDefaultMonth}
                className={inputClass('date_of_birth')}
              />
            </Row>

            <Row name="date_of_appointment" label="Date of Appointment" required pdfValue={form.date_of_appointment}
              feedback={feedback('date_of_appointment', { hint: "The employee's first day of employment.", ok: 'Looks good.' })}>
              <DateField
                name="date_of_appointment"
                value={form.date_of_appointment}
                onChange={(v) => setField('date_of_appointment', v)}
                onDone={() => touch('date_of_appointment')}
                fromDate={APPOINTMENT_FROM}
                toDate={appointmentTo()}
                defaultMonth={new Date()}
                className={inputClass('date_of_appointment')}
              />
            </Row>

            <Row name="id_type" label="Identification Type" required pdfValue={form.id_type}>
              <select
                id="id_type"
                data-testid="input-id_type"
                value={form.id_type}
                onChange={handleIdTypeChange}
                className="emp-input emp-select emp-w-md"
              >
                {Object.values(ID_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Row>

            <Row name="id_number" label={idLabel} required pdfValue={form.id_number}
              feedback={feedback('id_number', { hint: idHint, ok: idOk })}>
              {textInput('id_number', idFormatter(form.id_type), {
                inputMode: idTypeUsesSaIdNumber(form.id_type) ? 'numeric' : 'text',
                placeholder: idTypeUsesSaIdNumber(form.id_type) ? '13-digit ID number' : 'Document number',
              })}
            </Row>

            {form.id_type === ID_TYPES.PASSPORT && (
              <Row name="passport_country" label="Passport Country Code" required pdfValue={countryLabel(form.passport_country)}
                feedback={feedback('passport_country', { hint: 'The country that issued the passport (search by code or name).', ok: 'Looks good.' })}>
                <SearchSelect
                  name="passport_country"
                  selectedKey={form.passport_country}
                  display={countryLabel(form.passport_country)}
                  placeholder="Select country..."
                  searchPlaceholder="Search country or code..."
                  emptyText="No country found."
                  items={COUNTRY_ITEMS}
                  onSelect={(item) => setField('passport_country', item.key)}
                  onDone={() => touch('passport_country')}
                  className={inputClass('passport_country')}
                />
              </Row>
            )}

            <Row name="income_tax_number" label="Income Tax Number" optional pdfValue={form.income_tax_number}
              feedback={feedback('income_tax_number', { hint: 'Leave blank if not currently registered for income tax.', ok: 'Looks good — valid tax number format.' })}>
              {textInput('income_tax_number', formatDigits(10), { inputMode: 'numeric', placeholder: '10-digit tax number' })}
            </Row>

            <Row name="job_title" label="Job Title" required pdfValue={form.job_title}
              feedback={feedback('job_title', { hint: "Printed on the employee's payslip, e.g. Bookkeeper or CEO.", ok: 'Looks good.' })}>
              {textInput('job_title', formatJobTitle)}
            </Row>

            <Row name="email" label="Email" required pdfValue={form.email}
              feedback={feedback('email', { hint: 'Expected format: a valid email address (e.g. yourname@example.com). A copy of this form is emailed here.', ok: 'Looks good — valid email format.' })}>
              <input
                id="email" name="email" type="email" data-testid="input-email" autoComplete="email" inputMode="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value.replace(/\s/g, ''))}
                onBlur={blurTrim('email', { lower: true })}
                className={cn('emp-input', inputClass('email'))}
              />
            </Row>

            <Row name="cellphone" label="Cellphone No." required pdfValue={form.cellphone}
              feedback={feedback('cellphone', { hint: 'Expected format: 10 digits starting with 0 (e.g. 0821234567).', ok: 'Looks good — valid South African cellphone format.' })}>
              {textInput('cellphone', formatCellphone, { type: 'tel', inputMode: 'numeric', autoComplete: 'tel' })}
            </Row>
          </section>

          {/* ===================== Alternative Contact Details ===================== */}
          <section className="emp-section">
            <SectionTitle>Alternative Contact Details</SectionTitle>

            <Row name="alt_cellphone" label="Alternative Cell Number" optional pdfValue={form.alt_cellphone}
              feedback={feedback('alt_cellphone', { hint: 'Expected format: 10 digits starting with 0 (e.g. 0821234567).', ok: 'Looks good — valid South African cellphone format.' })}>
              {textInput('alt_cellphone', formatCellphone, { type: 'tel', inputMode: 'numeric' })}
            </Row>

            <Row name="alt_email" label="Alternative Email Address" optional pdfValue={form.alt_email}
              feedback={feedback('alt_email', { hint: 'Expected format: a valid email address (e.g. yourname@example.com).', ok: 'Looks good — valid email format.' })}>
              <input
                id="alt_email" name="alt_email" type="email" data-testid="input-alt_email" inputMode="email"
                value={form.alt_email}
                onChange={(e) => setField('alt_email', e.target.value.replace(/\s/g, ''))}
                onBlur={blurTrim('alt_email', { lower: true })}
                className={cn('emp-input', inputClass('alt_email'))}
              />
            </Row>
          </section>

          {/* ===================== Payment method + Bank Account Details ===================== */}
          <section className="emp-section">
            <Row name="payment_method" label="Payment Method" pdfValue={form.payment_method} className="emp-row-first">
              <select id="payment_method" value="EFT" disabled className="emp-input emp-select emp-w-sm" data-testid="input-payment_method">
                <option value="EFT">EFT</option>
              </select>
            </Row>

            <SectionTitle>Bank Account Details</SectionTitle>

            <Row name="bank_name" label="Bank" required pdfValue={form.bank_name}
              feedback={feedback('bank_name', { ok: 'Looks good.' })}>
              <SearchSelect
                name="bank_name"
                selectedKey={form.bank_name}
                display={form.bank_name}
                placeholder="Select bank..."
                searchPlaceholder="Search bank..."
                emptyText="No bank found."
                items={BANK_ITEMS}
                onSelect={(item) => setForm((f) => ({ ...f, bank_name: item.key, branch_code: item.branchCode }))}
                onDone={() => touch('bank_name')}
                className={inputClass('bank_name')}
              />
            </Row>

            <Row name="account_number" label="Account Number" required pdfValue={form.account_number}
              feedback={feedback('account_number', {
                hint: bankRule
                  ? `${bankRule.name}: ${bankRule.description}${bankRule.note ? ` — ${bankRule.note}` : ''}`
                  : 'Select the bank first.',
                ok: bankRule ? `Looks good — ${bankRule.name}: ${bankRule.description}.` : 'Looks good.',
              })}>
              {textInput('account_number', formatDigits(16), { inputMode: 'numeric', placeholder: 'Account number (digits only)' })}
            </Row>

            <Row name="branch_code" label="Branch Code" required pdfValue={form.branch_code}
              feedback={feedback('branch_code', { hint: 'Filled in automatically when the bank is selected (universal branch code).', ok: 'Universal branch code — filled in automatically.' })}>
              <input id="branch_code" type="text" value={form.branch_code} readOnly tabIndex={-1} className="emp-input emp-locked emp-w-md" data-testid="input-branch_code" />
            </Row>

            <Row name="account_type" label="Account Type" required pdfValue={form.account_type}
              feedback={feedback('account_type')}>
              <select
                id="account_type" data-testid="input-account_type"
                value={form.account_type}
                onChange={(e) => { setField('account_type', e.target.value); touch('account_type'); }}
                onBlur={() => touch('account_type')}
                className={cn('emp-input emp-select emp-w-md', inputClass('account_type'), !form.account_type && 'emp-placeholder')}
              >
                <option value="" disabled>Select account type...</option>
                {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Row>

            <Row name="holder_relationship" label="Holder Relationship" required pdfValue={form.holder_relationship}>
              <select id="holder_relationship" value={HOLDER_RELATIONSHIP} disabled className="emp-input emp-select emp-w-md" data-testid="input-holder_relationship">
                <option value={HOLDER_RELATIONSHIP}>{HOLDER_RELATIONSHIP}</option>
              </select>
            </Row>
          </section>

          {/* ===================== Residential Address ===================== */}
          <section className="emp-section">
            <SectionTitle>Residential Address</SectionTitle>

            <div className="emp-pair">
              <Row name="unit_number" label="Unit Number" optional pdfValue={form.unit_number}
                feedback={feedback('unit_number')}>
                {textInput('unit_number', formatAddress, { onBlur: blurTrim('unit_number', { pad: true }) })}
              </Row>
              <Row name="complex" label="Complex" optional pdfValue={form.complex}
                feedback={feedback('complex')}>
                {textInput('complex', formatAddress)}
              </Row>
            </div>

            <div className="emp-pair">
              <Row name="street_number" label="Street Number" required pdfValue={form.street_number}
                feedback={feedback('street_number', { ok: 'Looks good.' })}>
                {textInput('street_number', formatAddress, { onBlur: blurTrim('street_number', { pad: true }) })}
              </Row>
              <Row name="street" label="Street" required pdfValue={form.street}
                feedback={feedback('street', { ok: 'Looks good.' })}>
                {textInput('street', formatAddress, { onFocus: handleStreetFocus, onBlur: handleStreetBlur })}
              </Row>
            </div>

            <Row name="suburb" label="Suburb or District" required pdfValue={form.suburb}
              feedback={feedback('suburb', { ok: 'Looks good.' })}>
              {textInput('suburb', formatAddress)}
            </Row>

            <Row name="city" label="City or Town" required pdfValue={form.city}
              feedback={feedback('city', { ok: 'Looks good.' })}>
              {textInput('city', formatAddress)}
            </Row>

            <Row name="postal_code" label="Postal Code" required pdfValue={form.postal_code}
              feedback={feedback('postal_code', { hint: '4 digits, e.g. 2196.', ok: 'Looks good.' })}>
              {textInput('postal_code', formatDigits(4), { inputMode: 'numeric', className: cn('emp-input emp-w-sm', inputClass('postal_code')) })}
            </Row>

            <Row name="country" label="Country" pdfValue={form.country}>
              <input id="country" type="text" value="South Africa" disabled className="emp-input emp-locked emp-w-md" data-testid="input-country" />
            </Row>
          </section>

          {/* ===================== Remuneration Package (all mandatory) ===================== */}
          <section className="emp-section">
            <SectionTitle>Remuneration Package</SectionTitle>

            <Row name="gross_salary" label="Gross Salary" required pdfValue={formatRand(form.gross_salary)}
              feedback={feedback('gross_salary', {
                hint: `The gross salary ${{ Weekly: 'per week', Fortnightly: 'per fortnight' }[form.pay_frequency] || 'per month'}, before deductions. Numbers only, e.g. 25000 or 25000.50.`,
                ok: 'Looks good.',
              })}>
              <input
                id="gross_salary" name="gross_salary" type="text" inputMode="decimal" data-testid="input-gross_salary" autoComplete="off"
                value={salaryFocused ? form.gross_salary : formatRand(form.gross_salary)}
                onChange={formatted('gross_salary', formatAmountInput)}
                onFocus={() => setSalaryFocused(true)}
                onBlur={() => {
                  setSalaryFocused(false);
                  setForm((f) => ({ ...f, gross_salary: canonicalAmount(f.gross_salary) }));
                  touch('gross_salary');
                }}
                placeholder="0.00"
                className={cn('emp-input emp-w-md', inputClass('gross_salary'))}
              />
            </Row>

            <Row name="uif_paye" label="Employee wants to pay UIF and PAYE?" required pdfValue={form.uif_paye}
              feedback={feedback('uif_paye')}>
              <select
                id="uif_paye" data-testid="input-uif_paye"
                value={form.uif_paye}
                onChange={(e) => { setField('uif_paye', e.target.value); touch('uif_paye'); }}
                onBlur={() => touch('uif_paye')}
                className={cn('emp-input emp-select emp-w-sm', inputClass('uif_paye'))}
              >
                {UIF_PAYE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Row>
          </section>

          {/* SEND (bottom right) - hidden in the PDF */}
          <div className="emp-actions no-pdf">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              data-testid="button-send"
              className="btn-shimmer-orange emp-send-btn"
            >
              <Send className="w-5 h-5" />
              <span>{sending ? 'Sending...' : 'SEND'}</span>
            </button>
          </div>

          <div className="letterhead-footer-container">
            <img src="/images/letterhead-footer.png" alt="TC Auditors Footer" className="letterhead-footer" />
          </div>
        </div>
      </div>

      {/* Sending overlay (also hides the PDF capture) */}
      {sending && (
        <div className="emp-sending-overlay" data-testid="sending-overlay">
          <div className="spinner"></div>
          <p className="emp-sending-title">Sending the employee details...</p>
          <p className="emp-sending-text">Please wait while we generate and email the PDF.</p>
        </div>
      )}

      {/* "Please complete" / error modal */}
      {problems && (
        <div className="modal-overlay" data-testid="problems-dialog" onClick={closeProblems}>
          <div className="modal-content emp-problem-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{problems.title}</h2>
            <p className="modal-description">{problems.intro}</p>
            {problems.items.length > 0 && (
              <ol className="emp-problem-list">
                {problems.items.map((item) => <li key={item.name}>{item.text}</li>)}
              </ol>
            )}
            <div className="modal-actions">
              <button type="button" className="send-btn" onClick={closeProblems} data-testid="button-problems-ok">OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Success modal (as on the Power of Attorney / Debit Order Mandate sites) */}
      {recipients && (
        <div className="modal-overlay" data-testid="success-dialog">
          <div className="modal-content success-modal">
            <h2>New Employee File Successfully Sent</h2>
            <p className="modal-description success-message">
              Thank you. The employee's details have been sent to TC Auditors for payroll, with a PDF copy of this form.
            </p>

            <div className="email-recipients-confirmation" data-testid="email-recipients-confirmation">
              <p className="email-recipients-heading">A copy was sent to:</p>
              {recipients.map((r, idx) => (
                <div key={idx} className="email-recipient-item" data-testid={`email-recipient-${idx}`}>
                  <Check className="email-recipient-check" />
                  <span>{r.label}: <strong>{r.email}</strong></span>
                </div>
              ))}
            </div>

            <p className="modal-description success-message">
              Please check your inbox (and spam/junk folder) for your copy.
              <small>You may close this tab/screen on your browser now.</small>
            </p>

            <div className="modal-actions">
              <button type="button" data-testid="button-thank-you" onClick={handleThankYou} className="btn-shimmer-orange emp-thank-you-btn">
                <span>Thank You!</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeForm;
